// Whether a merchant delivers to the user's saved address(es). The buyer
// home + category surfaces use this to render a "request delivery" notice
// on out-of-zone merchants instead of hiding them, so users still discover
// the catalog and Neobox captures demand signals from outside current
// coverage.

const isPointInRadius = (point, center, radiusMeters) => {
  const toRad = (x) => (x * Math.PI) / 180;

  const R = 6371000; // Earth radius in meters
  const dLat = toRad(center.lat - point.lat);
  const dLng = toRad(center.lng - point.lng);
  const lat1 = toRad(point.lat);
  const lat2 = toRad(center.lat);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c <= radiusMeters;
};

const isPointInPolygon = (point, polygon) => {
  let inside = false;
  const { lat: x, lng: y } = point;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lat;
    const yi = polygon[i].lng;
    const xj = polygon[j].lat;
    const yj = polygon[j].lng;
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }
  return inside;
};

const cleanPolygon = (polygon) => {
  if (!Array.isArray(polygon) || polygon.length <= 1) {
    return polygon || [];
  }
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (first.lat === last.lat && first.lng === last.lng) {
    return polygon.slice(0, -1);
  }
  return polygon;
};

/**
 * Returns true if at least one of the user's saved locations falls inside
 * the merchant's currently active service zone. Returns true (permissive)
 * when the user has no saved location yet — we only flag a merchant as
 * out-of-zone when we have evidence the buyer's address isn't covered.
 */
export const isMerchantServiceable = (merchant, user) => {
  if (
    !merchant?.zones
    || !merchant?.activeZoneType
    || !Array.isArray(merchant.zones)
    || merchant.zones.length === 0
  ) {
    // Without zones declared, treat the merchant as available — the
    // delivery decision happens at checkout.
    return true;
  }

  const userLocations = user?.locations || [];
  if (userLocations.length === 0) {
    return true;
  }

  const activeZoneType = merchant.activeZoneType;
  for (const loc of userLocations) {
    const point = { lat: loc.latitude, lng: loc.longitude };
    for (const zone of merchant.zones) {
      if (
        activeZoneType === 'radius'
        && zone.type === 'radius'
        && zone.center
        && zone.radius
      ) {
        if (isPointInRadius(point, zone.center, zone.radius)) {
          return true;
        }
      } else if (
        activeZoneType === 'geofence'
        && zone.type === 'geofence'
        && Array.isArray(zone.polygon)
      ) {
        if (isPointInPolygon(point, cleanPolygon(zone.polygon))) {
          return true;
        }
      }
    }
  }

  return false;
};
